import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { useIsPhone } from "../../hooks/useIsPhone";

/** Width the email templates are designed for. */
const EMAIL_WIDTH = 600;

interface ScaledEmailFrameProps {
  srcDoc: string;
  title: string;
  /** Passed through to the iframe; omit for no sandbox attribute. */
  sandbox?: string;
}

/**
 * Email preview iframe that fills its parent box.
 *
 * Desktop: the plain 100% x 100% iframe. Phone: the email lays out at its
 * real 600 px width and is scaled down to the box width, the way phone mail
 * apps fit wide emails, so the preview never pans sideways. The template
 * itself is unchanged.
 */
export function ScaledEmailFrame({ srcDoc, title, sandbox }: ScaledEmailFrameProps) {
  const isPhone = useIsPhone();
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxSize, setBoxSize] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    if (!isPhone || !boxRef.current) return;
    const node = boxRef.current;
    const observer = new ResizeObserver(([entry]) => {
      setBoxSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [isPhone]);

  if (!isPhone) {
    return (
      <iframe
        srcDoc={srcDoc}
        title={title}
        sandbox={sandbox}
        style={{ width: "100%", height: "100%", border: "none" }}
      />
    );
  }

  const scale = boxSize ? Math.min(1, boxSize.width / EMAIL_WIDTH) : 1;
  return (
    <Box ref={boxRef} sx={{ width: "100%", height: "100%", overflow: "hidden" }}>
      <iframe
        srcDoc={srcDoc}
        title={title}
        sandbox={sandbox}
        style={{
          width: EMAIL_WIDTH,
          height: boxSize ? boxSize.height / scale : "100%",
          border: "none",
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      />
    </Box>
  );
}
